import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, hasStrokeDraw } from '../skillUsage';

export const ratingAndProgressFillSkill: CodeoSkill = {
    name: SKILL_NAMES.ratingAndProgressFill,
    kind: 'codeo',
    description:
        'Fill a rating out of five or sweep a circular progress ring to its value — for a score, ' +
        'a review average, a completion figure, or a share expressed as part of a whole.',
    checkLooksFor:
        'either a stroke drawn to a length, or a width tween on markup that names stars or a rating',
    documentUsesSkill: check(
        (script, markup) =>
            hasStrokeDraw(script) || (/width:/.test(script) && /★|star|rating/i.test(markup)),
    ),
    instructions: `# Rating And Progress Fill

Two shapes of the same idea: a row of marks filling one at a time, or a ring sweeping round to a fraction. Both say "this much out of that much", which a bare number cannot — the empty part is doing as much work as the full part.

Use a rating row when the total is small and countable (5 stars, 10 seats). Use a ring when the total is abstract (a percentage, a share). For anything larger or less regular, \`${SKILL_NAMES.dataAsMetaphor}\` will read better than either.

## Markup

The rating row is two identical rows stacked, the filled one clipped. Each star is its own span, and the row is sized to its own content — both matter, and the Pitfalls explain why. The ring is a stroked circle with a dash offset.

\`\`\`html
<div id="rating" style="position: absolute; left: 300px; top: 400px; width: max-content; height: 110px;">
  <div style="display: flex; gap: 18px; font-family: var(--l5-font-0), sans-serif; font-size: 96px; line-height: 1; color: var(--l5-color-1, #d6dbe3);"><span>★</span><span>★</span><span>★</span><span>★</span><span>★</span></div>
  <div id="rating-fill" style="position: absolute; left: 0; top: 0; height: 100%; overflow: hidden;">
    <div style="display: flex; gap: 18px; width: max-content; font-family: var(--l5-font-0), sans-serif; font-size: 96px; line-height: 1; color: var(--l5-color-2, #f5a524);"><span>★</span><span>★</span><span>★</span><span>★</span><span>★</span></div>
  </div>
</div>

<svg id="ring" viewBox="0 0 240 240" style="position: absolute; left: 1180px; top: 360px; width: 300px; height: 300px; rotate: -90deg;">
  <circle cx="120" cy="120" r="100" fill="none" stroke="var(--l5-color-1, #d6dbe3)" stroke-width="20" />
  <circle id="ring-value" cx="120" cy="120" r="100" fill="none" stroke="var(--l5-color-2, #38bdf8)" stroke-width="20" stroke-linecap="round" />
</svg>
\`\`\`

The \`rotate: -90deg\` on the SVG starts the ring at twelve o'clock; use the standalone property, not \`transform\`, since nothing here tweens the SVG itself.

## Timeline code

Derive the fill percentage from the value — \`RATING / 5\`, never a number you eyeballed. 4.5 out of 5 is **90%**, not 50%; a hand-picked percentage is the most common way this ends up showing a figure the headline contradicts.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const RATING = 4.5;
const RATING_OUT_OF = 5;
const RING_FRACTION = 0.72;
const ringEl = document.querySelector('#ring-value');
const circumference = Math.ceil(2 * Math.PI * 100) + 2;

gsap.set('#rating-fill', { width: 0 });
gsap.set(ringEl, { strokeDasharray: circumference, strokeDashoffset: circumference + 1 });

tl.to('#rating-fill', {
    width: \`\${(RATING / RATING_OUT_OF) * 100}%\`,
    duration: 0.85,
    ease: 'power2.out',
}, 0.3);

tl.to(ringEl, {
    strokeDashoffset: circumference * (1 - RING_FRACTION),
    duration: 1.1,
    ease: 'power3.out',
}, 0.45);

window.__timelines = { main: tl };
\`\`\`

Tweening \`width\` on the clip — rather than the stars themselves — is what lets a half star read as half.

## Adapt

- **Stepped rating**: for whole values only, stagger the marks in individually with a small \`back.out\` each rather than wiping the row. Reads as counting; the wipe reads as measuring.
- **Ring thickness**: 16–24px at this radius. Thinner reads as a UI spinner, thicker as a donut chart.
- **Value in the ring**: a figure at the centre, counting up in step with the sweep — see \`${SKILL_NAMES.counterTickUp}\` for the landing.
- **Multiple rings**: concentric rings sweeping together compare two shares. Two is the limit; three is a dashboard.
- **Ease**: \`power3.out\` arrives quickly and settles, so the *value* is what registers. \`power1.inOut\` reads as a machine measuring and suits a progress figure.
- **Overshoot the empty state**: leaving the unfilled marks visibly present is the point. Hiding them turns a rating into a row of stars.

## Pitfalls

- **The clip percentage is of the row's box, so the box must be exactly the row.** \`width: max-content\` on \`#rating\` is what makes 90% mean 90% *of the stars*. Give the row a round number like 620px while the glyphs only span ~480px and the clip clears the last star well before 100% — the fill then shows five stars at any rating above about 77%.
- **Each star needs its own element.** \`★★★★★\` as one text node is a single anonymous flex item, so \`gap\` has nothing to apply between and the marks render as a contiguous run at whatever tracking the font ships.
- The clipped row needs \`width: max-content\` of its own. Without it the inner flex row inherits the shrinking clip's width and the stars compress toward each other instead of being progressively revealed.
- Never hand-pick the fill percentage. Derive it from the value; a 4.5 filled to 50% is a graphic that argues with its own headline.
- Never build the ring sweep with CSS \`animation\`; the dash-offset tween is the seek-safe version.
- The dash circumference must be computed from the same radius the markup uses. A hardcoded value that drifts from \`r\` leaves the ring short or overlapping. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- Don't animate the *empty* row as well. It should already be there so the fill has something to fill.
- Both fill shapes need the value to be legible on landing: a 4.5 with no numeral beside it is guesswork.
- One measure per graphic. A rating *and* a ring *and* a bar is a dashboard.
- Fills that complete and hold leave the frame static — see \`${SKILL_NAMES.ambientFloatLoop}\`.
- Don't round up visually. A ring at 72% must stop at 72%, not near it.`,
};
